<script setup>
import {ref, computed, onMounted, watch} from 'vue';

const props = defineProps({
  config: {
    required: true
  },
  modelValue: {
    default: ''
  }
});

const emit = defineEmits(['update:modelValue']);


const defaultSettings = {
  classes: {
    error: 'has-error'
  },
  allowedCharacters: /./,
  hasError: false,
  height: 4,
  hasClearButton: true,
  line: true,
  scale: true,
  labelOffset: {
    top: 0.625,
    left: 0.5
  },
  color: {
    focusColor: '#128CED',
    lineColor: '#128CED',
    errorColor: '#ff0000',
    blurredColor: 'black',
    blurredColorLine: 'rgba(3, 23, 40, 0.40)'
  },
  label:'',
};


const inputValue = ref(''); // Holds the input's current value
const hasFocus = ref(false);
const hasContent = computed(() => {
  return inputValue.value !== undefined && inputValue.value !== null && inputValue.value !== '';
})


const settings = computed(() => {
  return Object.assign({}, defaultSettings, props.config);
});

const hasLabel = computed(() => {
  return settings.value.label !== '';
});

const activeLabelClasses = computed(() => ({
  'label__active--canscale': settings.value.scale
}));


const containerClasses = computed(() => ({
  'has-line': settings.value.line,
  'input__container--focus': hasFocus.value,
  'input__container--content': hasContent.value,
  'input__container--no-label': !hasLabel.value,
  [settings.value.classes.error]: settings.value.hasError
}));

const labelName = computed(() => {
  return props.config.name ?? settings.value.label.toLowerCase();
});

const LineStyle = computed(() => ({
  'background-color': settings.value.hasError ? settings.value.color.errorColor : settings.value.color.lineColor
}));

const labelColor = computed(() => {
  if (!settings.value.hasError) {
    return hasFocus.value ? settings.value.color.focusColor : settings.value.color.blurredColor;
  }
  return settings.value.color.errorColor;
});

const activeLabelStyle = computed(() => ({
  top: settings.value.labelOffset.top + 'em',
  left: settings.value.labelOffset.left + 'em',
  color: labelColor.value
}));

const inputContainerStyle = computed(() => ({
  height: settings.value.height + 'em'
}));

function clear() {
  inputValue.value = ''; // Clear the input field
  hasFocus.value = false;
}

function input(e) {
  hasFocus.value = true;
  const value = e.target.value;
  /*console.log(`Raw input: ${value}`);  // Log the raw input
  console.log(`Allowed characters regex: ${settings.value.allowedCharacters}`);  // Log the regex*/

  // Sanitize the input value based on allowedCharacters directly
  const sanitizedValue = value.replace(settings.value.allowedCharacters, '');
  /*
    console.log(`Sanitized value: ${sanitizedValue}`);  // Log the sanitized value
  */

  inputValue.value = sanitizedValue;
  emit('update:modelValue', sanitizedValue);
}


onMounted(() => {
  inputValue.value = props.modelValue
})

watch(() => props.modelValue, (newValue) => {
  inputValue.value = newValue;
});


</script>

<template>
  <div :style="!hasFocus ? `border-bottom: ${settings.color.blurredColorLine}  solid 2px;` : ''">
    <div class="input__container" :class="[containerClasses, { 'input__container--has-label': hasLabel }]" :style="inputContainerStyle">
      <i class="icon clear__icon bi bi-x-circle" @mousedown.stop.prevent="clear" v-if="settings.hasClearButton"></i>
      <div class="line__icon" :style="LineStyle" v-if="settings.line"></div>
      <div class="slot-container" ref="input-container">
        <input
          @focus="hasFocus = true"
          @blur="hasFocus = false"
          @input="e => input(e)"
          v-model="inputValue"
          :id="labelName"
          v-bind="$attrs"
        />
      </div>
      <template v-if="hasLabel">
        <label class="label__placeholder" :for="labelName">{{ config.label }}</label>
        <label class="label__active" :class="activeLabelClasses" :style="activeLabelStyle" :for="labelName">
          {{ config.label }}
        </label>
      </template>
    </div>
  </div>
</template>

<style scoped>
.input__container {
  position: relative;
  padding: 0 0.5em;
  transition: 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}

.input__container .slot-container {
  height: 100%;
}

.input__container .slot-container input {
  height: 100%;
  font-size: 1em;
  padding: 0;
  border: 0;
  display: block;
  width: 100%;
  position: relative;
  background-color: transparent;
  transition: 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}


.input__container + .input__container:before {
  display: none;
}

.input__container.input__container--focus .line__icon {
  transform: scaleX(1);
}


.input__container.input__container--content label.label__placeholder {
  opacity: 0;
}

.input__container.input__container--content .label__active {
  opacity: 1;
}


.input__container--has-label.input__container--content input,
.input__container--has-label.input__container--content .label__placeholder {
  transform: translate3d(0, 1em, 0);
}


.input__container.input__container--focus.input__container--content label.label__placeholder {
  opacity: 0;
}

.input__container.input__container--focus.input__container--content .clear__icon {
  opacity: 1;
}

.input__container.input__container--focus.input__container--content .line__icon {
  transform: scaleX(1);
}


.icon {
  opacity: 0;
  position: absolute;
  height: 1.5em;
  width: 1.5em;
  right: 1em;
  top: 50%;
  z-index: 1;
  transform: translate3d(0, -50%, 0);
}

.line__icon {
  position: absolute;
  height: 0.125em;
  left: 0.5em;
  right: 0.5em;
  bottom: 0;
  z-index: 9;
  border-radius: 100px;
  background-color: #128CED;
  transition: 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  transform: scaleX(0);
  transform-origin: 0 0;
}

.clear__icon {
  cursor: pointer;
  font-size: 1.5em;
  z-index: 1;
  color: rgba(1, 16, 30, 0.24);
  text-align: center;
  transition: 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}

input {
  font-size: 1em;
  color: black;
  line-height: 1.5em;
}

input:focus {
  outline: none;
}

label {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  user-select: none;
  transition: 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
}

label.label__placeholder {
  top: 50%;
  transform: translate(0, -50%);
  color: rgba(3, 23, 40, 0.40);
  left: 0.5em;
}

label.label__active {
  font-size: 1em;
  color: rgba(3, 23, 40);
  font-weight: 500;
  line-height: 1em;
  opacity: 0;
  transform: translate3d(0, 0.1875em, 0);
  transform-origin: 0 0;
}

</style>